import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL, BASE_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DEFAULT_WA_WS_BASE = 'https://app.chattitude.ai/slickbots-ws';

function waWsBaseUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) {
    return BASE_URL || DEFAULT_WA_WS_BASE;
  }
  return `${t}/slickbots-ws`;
}

const WhatsAppCredentialsModal = ({
  open,
  onClose,
  onCredentialsSaved,
  tenantId,
  agentId,
  productName = 'Chattitude',
  resellerBaseUrl,
}) => {
  const waWsBase = useMemo(() => waWsBaseUrl(resellerBaseUrl), [resellerBaseUrl]);
  const [credentials, setCredentials] = useState({
    waApiBaseUrl: '',
    waSessionId: ''
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [encryptedAgentId, setEncryptedAgentId] = useState(null);
  const [encryptedTenantId, setEncryptedTenantId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenantId || !agentId) {
        setCredentials({ waApiBaseUrl: '', waSessionId: '' });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const jwtToken = Cookies.getJwtToken();
        const tenant = Cookies.getTenantId();
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

        const response = await axios.get(`${API_URL}/agent-whatsapp-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setCredentials({
          waApiBaseUrl: response.data.waApiBaseUrl || '',
          waSessionId: response.data.waSessionId || ''
        });
      } catch (err) {
        if (err.response?.status === 404) {
          setCredentials({ waApiBaseUrl: '', waSessionId: '' });
        } else {
          setCredentials({ waApiBaseUrl: '', waSessionId: '' });
          setError('Failed to fetch credentials.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (open && tenantId && agentId) {
      fetchCredentials();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open, tenantId, agentId]);

  useEffect(() => {
    const fetchEncryptedIds = async () => {
      if (!open || !agentId) return;
      try {
        const response = await axios.post('/agents/encrypt-ids', null, {
          params: { agentId }
        });
        setEncryptedAgentId(response.data.encryptedAgentId);
        setEncryptedTenantId(response.data.encryptedTenantId);
      } catch (err) {
        console.error('Error fetching encrypted IDs:', err);
        setEncryptedAgentId('');
        setEncryptedTenantId('');
      }
    };
    fetchEncryptedIds();
  }, [open, agentId]);

  const buildWebhookQuery = () => {
    if (!encryptedAgentId || !encryptedTenantId) return '';
    return [
      `agentId=${encodeURIComponent(encryptedAgentId)}`,
      `tenantId=${encodeURIComponent(encryptedTenantId)}`
    ].join('&');
  };

  const getWebhookUrl = () => {
    const q = buildWebhookQuery();
    return q ? `${waWsBase}/wa/webhook?${q}` : '';
  };

  const handleCopyWebhookUrl = () => {
    const url = getWebhookUrl();
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => setCopySuccess(true))
      .catch(err => console.error('Failed to copy:', err));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.waApiBaseUrl?.trim()) return 'API Base URL is required.';
    if (!credentials.waSessionId?.trim()) return 'Session ID is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const jwtToken = Cookies.getJwtToken();
      const tenant = Cookies.getTenantId();
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

      await axios.post(`${API_URL}/agent-whatsapp-credentials`, {
        tenantId: parseInt(tenantId, 10),
        agentId: parseInt(agentId, 10),
        provider: 'openwa',
        waApiBaseUrl: credentials.waApiBaseUrl.trim(),
        waSessionId: credentials.waSessionId.trim()
      }, { headers });

      setSnackbar({ open: true, message: 'WhatsApp credentials saved.', severity: 'success' });
      if (onCredentialsSaved) onCredentialsSaved();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save credentials.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          WhatsApp Credentials
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && (
            <Box>
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                OpenWA connection
              </Typography>
              <TextField
                fullWidth
                margin="dense"
                label="API Base URL"
                name="waApiBaseUrl"
                value={credentials.waApiBaseUrl}
                onChange={handleChange}
                placeholder="https://your-openwa-host.example.com"
                helperText="Base URL of your OpenWA API (no trailing slash)"
              />
              <TextField
                fullWidth
                margin="dense"
                label="Session ID"
                name="waSessionId"
                value={credentials.waSessionId}
                onChange={handleChange}
                placeholder="fb215709-5b7f-4836-bdfc-2118ecd97d34"
                helperText="OpenWA session UUID used for outbound replies"
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Webhook URL (configure in OpenWA)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={getWebhookUrl()}
                  InputProps={{ readOnly: true }}
                  placeholder="Loading encrypted webhook URL..."
                />
                <Tooltip title={copySuccess ? 'Copied!' : 'Copy webhook URL'}>
                  <IconButton onClick={handleCopyWebhookUrl} disabled={!getWebhookUrl()} color="primary">
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Setup instructions
              </Typography>
              <List dense sx={{ listStyleType: 'decimal', pl: 2 }}>
                <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                  <ListItemText primary={`In ${productName}, enter your OpenWA API Base URL and Session ID, then click Save & Close.`} />
                </ListItem>
                <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                  <ListItemText primary="In OpenWA, configure the webhook to POST message events to the Webhook URL above (agent and tenant IDs are encrypted in the URL)." />
                </ListItem>
                <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                  <ListItemText primary="Send a WhatsApp DM to the connected number. The agent should reply as a quoted message." />
                </ListItem>
                <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                  <ListItemText primary="Sessions close automatically after inactivity; end users do not need special commands." />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WhatsAppCredentialsModal;
